import React from 'react'
import { useTranslation } from 'react-i18next'
import { Form, FormInstance, Input } from 'antd'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { hasLinkErrorState } from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import { LinkAnnotation } from 'types/building'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'

type Props = {
  form: FormInstance
}

export default function LinkSetting({ form }: Props) {
  const { t } = useTranslation()

  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const setHasLinkError = useSetRecoilState(hasLinkErrorState)

  const scrollRef = React.useRef<HTMLDivElement>(null)
  const prevLinksLength = React.useRef(0)

  const links = Form.useWatch('links', form)

  React.useEffect(() => {
    if (links) {
      const normalizedLinks = links.map((item: LinkAnnotation | null) =>
        item == null
          ? { link: '', name: '' }
          : { link: item.link || '', name: item.name || '' }
      )
      setCurrentSelected((prev) => ({ ...prev, links: normalizedLinks }))
      if (
        prevLinksLength.current > 0 &&
        links.length > prevLinksLength.current
      ) {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      }
      prevLinksLength.current = links.length
    } else if (currentSelected?.links) {
      prevLinksLength.current = currentSelected.links.length
    }
  }, [links, setCurrentSelected])

  React.useEffect(() => {
    setTimeout(() => {
      form.validateFields()
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Form
      form={form}
      colon={false}
      labelCol={{ span: 5 }}
      labelAlign="left"
      wrapperCol={{ span: 19 }}
      onFieldsChange={(_, allFields) => {
        const hasError = allFields.some(
          (f) =>
            f.name &&
            Array.isArray(f.name) &&
            f.name[0] === 'links' &&
            f.errors &&
            f.errors.length > 0
        )
        setHasLinkError(hasError)
      }}
      initialValues={
        currentSelected?.links && currentSelected.links.length > 0
          ? currentSelected
          : {
              ...currentSelected,
              links: [
                {
                  link: currentSelected?.link || '',
                  name: currentSelected?.name || ''
                }
              ]
            }
      }
      className="flex flex-col w-full pl-[31px] pr-[50px] pt-[36px] "
      style={{ height: '447px' }}
    >
      <Form.List name="links">
        {(fields, { add, remove }) => (
          <>
            <div
              ref={scrollRef}
              className="max-h-[450px] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar"
            >
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  className="relative mb-4 pb-4 border-0 border-b border-[#EEEFF2] last:border-b-0 last:mb-0"
                >
                  <div className="flex flex-col">
                    <Form.Item
                      {...restField}
                      name={[name, 'link']}
                      label={t('building.inputURLAnnotation')}
                      rules={[
                        {
                          validator: Validators.linkAnnotationValidator(t)
                        }
                      ]}
                    >
                      <Input
                        placeholder="https://"
                        className="h-[38px] rounded-[6px] py-[9px] px-[16px] text-[14px] w-full"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      label={t('building.inputNameAnnotation')}
                      className="!mb-1"
                      rules={[
                        {
                          validator:
                            Validators.keywordsAndLengthCustomValidator(
                              t,
                              500,
                              undefined,
                              true
                            )
                        }
                      ]}
                    >
                      <Input
                        className="rounded-[6px] py-[9px] px-[16px] text-[14px] h-[38px] w-full"
                        placeholder={t('common.floor.placeHolder')}
                      />
                    </Form.Item>
                    {name > 0 && (
                      <div className="flex justify-end mt-0">
                        <Button
                          sizing="w-[84px] h-[32px] rounded-[4px]"
                          className="!bg-[#B80808] !text-white border-0 hover:opacity-80 text-[13px] font-bold"
                          onClick={() => remove(name)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 pb-2">
              <Button
                sizing="w-[160px] h-[40px]"
                onClick={() => add()}
                style={{
                  backgroundColor: '#657cff',
                  borderColor: '#657cff',
                  color: '#ffffff'
                }}
              >
                {t('translation.add')}
              </Button>
            </div>
          </>
        )}
      </Form.List>
    </Form>
  )
}
